import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export const Eyebrow = ({ children, className = "", tone = "#C26D52" }) => (
  <p
    className={`inline-flex items-center gap-2.5 font-mono-brand text-[11px] tracking-[0.28em] uppercase ${className}`}
    style={{ color: tone }}
  >
    <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone }} />
    {children}
  </p>
);

export const SectionTitle = ({ children, className = "", as: Tag = "h2" }) => (
  <Tag
    className={`font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-semibold tracking-tight leading-[1.08] text-[#0E1B31] ${className}`}
  >
    {children}
  </Tag>
);

const VARIANTS = {
  solid: "bg-[#0E1B31] text-[#F8F6F0] hover:bg-[#16294A] shadow-[0_14px_30px_-14px_rgba(14,27,49,0.6)]",
  copper: "bg-[#C26D52] text-white hover:bg-[#A9583F] shadow-[0_14px_30px_-14px_rgba(194,109,82,0.7)]",
  outline: "border border-[#0E1B31]/20 text-[#0E1B31] hover:border-[#0E1B31] bg-white/60 backdrop-blur",
  light: "bg-white text-[#0E1B31] hover:bg-[#F2EEE7] shadow-[0_14px_30px_-14px_rgba(14,27,49,0.35)]",
  ghost: "text-[#0E1B31] hover:text-[#C26D52] px-0",
};

export const GoldButton = ({ to, href, children, testId, variant = "solid", className = "", onClick, type, disabled }) => {
  const cls = `group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`;
  const content = (
    <>
      {children}
      <span className="relative flex h-5 w-5 items-center justify-center overflow-hidden">
        <ArrowRight className="h-4 w-4 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-6" />
        <ArrowRight className="absolute h-4 w-4 -translate-x-6 transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-0" />
      </span>
    </>
  );
  const Wrap = ({ children: c }) => (
    <motion.span whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} className="inline-flex">
      {c}
    </motion.span>
  );
  if (to) {
    return (
      <Wrap>
        <Link to={to} data-testid={testId} className={cls}>
          {content}
        </Link>
      </Wrap>
    );
  }
  if (href) {
    return (
      <Wrap>
        <a href={href} data-testid={testId} className={cls}>
          {content}
        </a>
      </Wrap>
    );
  }
  return (
    <Wrap>
      <button type={type || "button"} onClick={onClick} disabled={disabled} data-testid={testId} className={cls}>
        {content}
      </button>
    </Wrap>
  );
};

export const Chip = ({ children, tone = "#C26D52", tint = "#F7EFE9", className = "", testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-[12px] font-medium leading-none transition-colors duration-300 ${className}`}
    style={{ color: tone, background: tint, borderColor: `${tone}33` }}
  >
    {children}
  </span>
);
